import React, { useState, useEffect } from "react"
import {
  useGetAdminOrganizationsQuery,
  useLazyGetOrgsByScoreSetIdQuery,
  useUpdateCompositeScoreOrgMappingMutation,
} from "../../../app/services/meseeknaApiV2"

type OrgMappingModalProps = {
  scoreSetId: number
  scoreSetName: string
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}

const OrgMappingModal: React.FC<OrgMappingModalProps> = ({
  scoreSetId,
  scoreSetName,
  isOpen,
  onClose,
  onSave,
}) => {
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<number>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)

  const { data: orgsData, isLoading: orgsLoading } = useGetAdminOrganizationsQuery()
  const [fetchCurrentMappings] = useLazyGetOrgsByScoreSetIdQuery()
  const [updateMapping, { isLoading: isSaving }] = useUpdateCompositeScoreOrgMappingMutation()

  const organizations = orgsData?.data || []

  useEffect(() => {
    const loadCurrentMappings = async () => {
      if (!isOpen) return

      setInitialLoading(true)
      setError(null)

      try {
        const result = await fetchCurrentMappings(scoreSetId).unwrap()
        const currentOrgIds = new Set(
          (result.data || []).map((mapping) => mapping.organizationId)
        )
        setSelectedOrgIds(currentOrgIds)
      } catch (err) {
        console.error("Failed to load current mappings:", err)
        setSelectedOrgIds(new Set())
      } finally {
        setInitialLoading(false)
      }
    }

    loadCurrentMappings()
  }, [isOpen, scoreSetId, fetchCurrentMappings])

  const handleToggleOrg = (orgId: number) => {
    setSelectedOrgIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(orgId)) {
        newSet.delete(orgId)
      } else {
        newSet.add(orgId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    setSelectedOrgIds(new Set(organizations.map((org) => org.id)))
  }

  const handleClearAll = () => {
    setSelectedOrgIds(new Set())
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    try {
      await updateMapping({
        composite_score_set_id: scoreSetId,
        orgIdsArray: Array.from(selectedOrgIds),
      }).unwrap()
      onSave()
    } catch (err) {
      console.error("Failed to update org mappings:", err)
      setError("Failed to update organization mappings. Please try again.")
    }
  }

  if (!isOpen) return null

  const isLoading = orgsLoading || initialLoading

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Organization Mappings
            </h3>
            <p className="text-sm text-gray-500">
              Score Set: {scoreSetName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 cursor-pointer"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-gray-600">
              {selectedOrgIds.size} of {organizations.length} organizations selected
            </p>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={isLoading}
                className="text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer disabled:opacity-50"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={handleClearAll}
                disabled={isLoading}
                className="text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer disabled:opacity-50"
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md mb-4">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                Loading organizations...
              </div>
            ) : organizations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No organizations found
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {organizations.map((org) => (
                  <label
                    key={org.id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrgIds.has(org.id)}
                      onChange={() => handleToggleOrg(org.id)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-3 text-sm text-gray-900">
                      {org.organizationName}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 cursor-pointer disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default OrgMappingModal
